// Riftbound TCG taxonomy.
// Sources of truth come from the Riftcodex API at runtime (/api/facets).
// These are sane fallbacks that populate the UI before the first API response.

import {
  SET_RELEASE_CALENDAR,
  setLanguagePrintDate,
  setReleaseStatus as calendarSetReleaseStatus,
} from '../../shared/setReleaseCalendar.js';

export {
  constructedMetaPeriods,
  constructedMetaSetCodes,
  currentMetaPeriod,
  isConstructedMetaSetCode,
  metaPeriodByCode,
  metaPeriodFilterOptions,
  metaPeriodInclusiveEndDate,
  metaPeriodTimeRange,
  releasedMetaPeriods,
  timestampInMetaPeriod,
} from '../../shared/metaPeriods.js';
export type {
  ConstructedMetaPeriod,
  ConstructedMetaPeriodOption,
  MetaPeriodTimeRange,
} from '../../shared/metaPeriods.js';

export const CARD_TYPES = [
  'Unit',
  'Spell',
  'Legend',
  'Gear',
  'Battlefield',
  'Rune',
] as const;

export const SUPERTYPES = ['Champion', 'Signature', 'Basic', 'Token'] as const;

// Riftbound uses "domains" (think: factions/regions from Runeterra).
export const REGIONS = [
  'Shadow Isles',
  'Noxus',
  'Demacia',
  'Freljord',
  'Ionia',
  'Piltover & Zaun',
  'Bilgewater',
  'Targon',
  'Shurima',
  'Bandle City',
  'Runeterra',
] as const;

/** Classification domains (`classification.domain`); mirrors `/api/facets` fallback. */
export const DOMAINS = ['Body', 'Calm', 'Chaos', 'Fury', 'Mind', 'Order'] as const;

// Keyword abilities extracted from card text (e.g. [Ambush], [Rally]).
// The /api/facets endpoint provides the definitive list from cached card data.
export const KEYWORDS = [
  'Ambush',
  'Rally',
  'Overwhelm',
  'Elusive',
  'Fearsome',
  'Challenger',
  'Barrier',
  'Ephemeral',
  'Scout',
  'Lifesteal',
  'Tough',
  'Quick Attack',
  'Spellshield',
  'Regeneration',
] as const;

export const RARITIES = [
  'Common',
  'Uncommon',
  'Rare',
  'Epic',
  'Showcase',
] as const;

/** Available print languages for a set (BCP-47-ish codes). */
export type SetLanguage = 'en' | 'zh' | 'ko' | 'fr';

const EN: readonly SetLanguage[] = ['en'];
const EN_ZH: readonly SetLanguage[] = ['en', 'zh'];
const EN_ZH_FR: readonly SetLanguage[] = ['en', 'zh', 'fr'];
const EN_ZH_FR_KO: readonly SetLanguage[] = ['en', 'zh', 'fr', 'ko'];

/** Every language the lot picker offers. Set taxonomy orders known ones first. */
export const ALL_SET_LANGUAGES: readonly SetLanguage[] = ['en', 'zh', 'ko', 'fr'];

export const SET_LANGUAGE_LABELS: Record<SetLanguage, string> = {
  en: 'English',
  zh: 'Chinese',
  ko: 'Korean',
  fr: 'French',
};

/**
 * Local set taxonomy. Authoritative names still come from Riftcodex `/sets` via
 * `/api/facets` `setNames` at runtime — these are fallbacks + release calendar.
 *
 * `releaseStatus` is derived from dates (not stored):
 * - no `releaseDate` → released (promos / evergreen)
 * - today >= `releaseDate` → released
 * - today >= `previewDate` (optional) and before release → preview
 * - else → upcoming
 *
 * Release/preview dates themselves live in `shared/setReleaseCalendar.js`
 * (imported below), not here — that module is also consumed server-side
 * (deriving each card's `previewed` flag, SQL release ordering) so there is
 * exactly one place to update when a set's date changes or a new set is
 * announced. Add presentational fields (name/description/languages) here;
 * add the date to the shared calendar.
 */
export type SetReleaseStatus = 'released' | 'preview' | 'upcoming';

export type SetDefinition = {
  code: string;
  name: string;
  /** ISO date (YYYY-MM-DD, UTC) when the set releases / released. Sourced from the shared calendar. */
  releaseDate?: string;
  /** ISO date when official previews begin; enables `preview` between this and release. */
  previewDate?: string;
  /** Display override when the calendar date is approximate (e.g. "Q3 2027"). */
  releaseDateLabel?: string;
  /** Short marketing blurb for /sets cards, SSR shells, and meta descriptions. */
  description?: string;
  languages: readonly SetLanguage[];
};

type SetPresentation = Omit<SetDefinition, 'releaseDate' | 'previewDate' | 'releaseDateLabel'>;

const SET_PRESENTATION: readonly SetPresentation[] = [
  {
    code: 'ARC',
    name: 'Arcane',
    description: 'A 6-card Showcase champion set from the Arcane series, in English and Chinese.',
    languages: EN_ZH,
  },
  {
    code: 'OGN',
    name: 'Origins',
    description: 'The debut Riftbound TCG set: the original champion roster and launch card pool.',
    languages: EN_ZH_FR_KO,
  },
  {
    code: 'OGS',
    name: 'Origins: Proving Grounds',
    description: 'Origins: Proving Grounds starter product with its complete card list.',
    languages: EN_ZH_FR_KO,
  },
  {
    code: 'SFD',
    name: 'Spiritforged',
    description: 'Second expansion: new legends and a bigger card pool.',
    languages: EN_ZH_FR_KO,
  },
  {
    code: 'UNL',
    name: 'Unleashed',
    description: 'Third set with new champions and mechanics.',
    languages: EN_ZH_FR,
  },
  {
    code: 'VEN',
    name: 'Vendetta',
    description: 'Fourth expansion with new champions and rivalries.',
    languages: EN_ZH_FR,
  },
  {
    code: 'RAD',
    name: 'Radiance',
    description: 'Fifth set — own the stage under the brightest lights.',
    languages: EN_ZH_FR,
  },
  {
    code: 'LGC',
    name: 'Legacy',
    description: 'Sixth set — bear the banner and forge your legacy across Runeterra’s regions.',
    languages: EN_ZH_FR,
  },
  {
    code: 'PG2',
    name: 'Proving Grounds, 2nd Edition',
    description: 'Updated starter product with four Legacy champion decks (February 2027).',
    languages: EN_ZH_FR_KO,
  },
  {
    code: 'REC',
    name: 'The Reckoning',
    description: 'Seventh set — awaken the ancients with League’s biggest champions.',
    languages: EN_ZH_FR,
  },
  {
    code: 'S08',
    name: 'Set 8',
    description: 'Q3 2027 expansion — name and details still under wraps (Bandle City tease).',
    languages: EN,
  },
  {
    code: 'S09',
    name: 'Set 9',
    description: 'Q4 2027 expansion — name and details still under wraps.',
    languages: EN,
  },
  { code: 'JDG', name: 'Judge', languages: EN_ZH },
  { code: 'OPP', name: 'Organized Play', languages: EN },
  { code: 'PR', name: 'Product promos', languages: EN },
];

const CALENDAR_BY_CODE: Record<string, (typeof SET_RELEASE_CALENDAR)[number]> = Object.fromEntries(
  SET_RELEASE_CALENDAR.map((s) => [s.code.toUpperCase(), s]),
);

export const SETS: readonly SetDefinition[] = SET_PRESENTATION.map((set) => {
  const cal = CALENDAR_BY_CODE[set.code.toUpperCase()];
  return {
    ...set,
    releaseDate: cal?.releaseDate,
    previewDate: cal?.previewDate,
    releaseDateLabel: cal?.releaseDateLabel,
  };
});

/**
 * Expansions linked from `/sets` and prioritized in sitemap / bot HTML.
 * PG2 is intentionally omitted (collection product, not the sets index).
 */
export const FEATURED_SET_CODES = [
  'ARC',
  'OGN',
  'SFD',
  'UNL',
  'VEN',
  'RAD',
  'LGC',
  'REC',
  'S08',
  'S09',
] as const;

const SET_BY_CODE: Record<string, SetDefinition> = Object.fromEntries(
  SETS.map((s) => [s.code.toUpperCase(), s]),
);

export function setReleaseStatus(setCode: string | undefined): SetReleaseStatus {
  if (!setCode?.trim()) return 'released';
  return calendarSetReleaseStatus(setCode) as SetReleaseStatus;
}

export function setIsUpcoming(setCode: string | undefined): boolean {
  return setReleaseStatus(setCode) === 'upcoming';
}

export function setIsPreview(setCode: string | undefined): boolean {
  return setReleaseStatus(setCode) === 'preview';
}

export function setIsPreviewOrUpcoming(setCode: string | undefined): boolean {
  const status = setReleaseStatus(setCode);
  return status === 'upcoming' || status === 'preview';
}

export const SET_RELEASE_DATE_BY_CODE: Record<string, string> = Object.fromEntries(
  SETS.flatMap((s) => (s.releaseDate ? [[s.code.toUpperCase(), s.releaseDate]] : [])),
);

const SET_RELEASE_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});

export function setReleaseDate(setCode: string | undefined): string | undefined {
  if (!setCode?.trim()) return undefined;
  return SET_RELEASE_DATE_BY_CODE[setCode.trim().toUpperCase()];
}

function formatReleaseDay(releaseDate: string): string {
  const [year, month, day] = releaseDate.split('-').map(Number);
  if (!year || !month || !day) return releaseDate;
  return SET_RELEASE_DATE_FORMATTER.format(new Date(Date.UTC(year, month - 1, day)));
}

export function setReleaseDateLabel(setCode: string | undefined): string | undefined {
  if (!setCode?.trim()) return undefined;
  const meta = SET_BY_CODE[setCode.trim().toUpperCase()];
  if (meta?.releaseDateLabel) return meta.releaseDateLabel;

  const releaseDate = meta?.releaseDate ?? setReleaseDate(setCode);
  if (!releaseDate) return undefined;
  return formatReleaseDay(releaseDate);
}

export type SetLanguageRelease = {
  language: SetLanguage;
  /** YYYY-MM-DD when the printing day is known. */
  date?: string;
  /** Formatted day, or an announced window such as "December 2026". */
  display: string;
};

/** One line per printed language. English is the catalog date; the others can differ. */
export function setLanguageSchedule(setCode: string | undefined): SetLanguageRelease[] {
  return setKnownLanguages(setCode).flatMap((language) => {
    const printed = setLanguagePrintDate(setCode, language);
    if (printed?.label) return [{ language, display: printed.label }];
    if (!printed?.date) return [];
    return [{ language, date: printed.date, display: formatReleaseDay(printed.date) }];
  });
}

/** True when two printings of this set do not share a day. */
export function setLanguageDatesDiffer(setCode: string | undefined): boolean {
  const keys = setLanguageSchedule(setCode).map((item) => item.date ?? item.display);
  return new Set(keys).size > 1;
}

export const SET_LANGUAGES_BY_CODE: Record<string, readonly SetLanguage[]> = Object.fromEntries(
  SETS.map((s) => [s.code.toUpperCase(), s.languages]),
) as Record<string, readonly SetLanguage[]>;

/** Languages documented for a set — used for labels, not as a hard picker filter. */
export function setKnownLanguages(setCode: string | undefined): readonly SetLanguage[] {
  if (!setCode?.trim()) return EN;
  return SET_LANGUAGES_BY_CODE[setCode.trim().toUpperCase()] ?? EN;
}

/**
 * Languages for a collection lot picker: the set's known languages first, then
 * the rest of the taxonomy so a collector is never blocked by stale set data.
 */
export function setLanguages(setCode: string | undefined): readonly SetLanguage[] {
  const known = setKnownLanguages(setCode);
  const rest = ALL_SET_LANGUAGES.filter((code) => !known.includes(code));
  return [...known, ...rest];
}

export function setLanguagesLabel(setCode: string | undefined): string | undefined {
  const languages = setKnownLanguages(setCode);
  if (!languages.length) return undefined;
  return languages.map((code) => SET_LANGUAGE_LABELS[code] ?? code).join(' · ');
}

export const SORT_OPTIONS = [
  { value: 'name-asc', label: 'Name (A→Z)' },
  { value: 'name-desc', label: 'Name (Z→A)' },
  { value: 'cost-asc', label: 'Energy (low→high)' },
  { value: 'cost-desc', label: 'Energy (high→low)' },
  { value: 'might-asc', label: 'Might (low→high)' },
  { value: 'might-desc', label: 'Might (high→low)' },
  { value: 'set-newest', label: 'Set (newest first)' },
  { value: 'number-asc', label: 'Collection №' },
] as const;

export type SortOption = (typeof SORT_OPTIONS)[number]['value'];

/** Collection-owned grid sorts (includes scan/recency and market price). */
export const COLLECTION_SORT_OPTIONS = [
  { value: 'scanned-desc', label: 'Recently scanned' },
  { value: 'name-asc', label: 'Name (A→Z)' },
  { value: 'name-desc', label: 'Name (Z→A)' },
  { value: 'cost-asc', label: 'Energy (low→high)' },
  { value: 'cost-desc', label: 'Energy (high→low)' },
  { value: 'might-asc', label: 'Might (low→high)' },
  { value: 'might-desc', label: 'Might (high→low)' },
  { value: 'price-desc', label: 'Price (high→low)' },
  { value: 'price-asc', label: 'Price (low→high)' },
  { value: 'set-newest', label: 'Set (newest first)' },
  { value: 'number-asc', label: 'Collection №' },
] as const;

export type CollectionSortOption = (typeof COLLECTION_SORT_OPTIONS)[number]['value'];

/** Default ordering for catalog/search grids using `SearchStats` (collection number ascending). */
export const DEFAULT_GRID_SORT = 'number-asc' satisfies SortOption;

/** URL slug for each set code — used in /sets/:slug routes. */
export const SET_CODE_TO_SLUG: Record<string, string> = {
  ARC: 'arcane',
  OGN: 'origins',
  OGS: 'origins-proving-grounds',
  SFD: 'spiritforged',
  UNL: 'unleashed',
  RAD: 'radiance',
  VEN: 'vendetta',
  LGC: 'legacy',
  PG2: 'proving-grounds-2nd-edition',
  REC: 'the-reckoning',
  S08: 'set-8',
  S09: 'set-9',
  JDG: 'judge-promo',
  OPP: 'organized-play-promo',
  PR: 'promo',
};

/** Reverse mapping from slug to uppercase set code — for /sets/:slug route param parsing. */
export const SET_SLUG_TO_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(SET_CODE_TO_SLUG).map(([k, v]) => [v, k]),
);

export { SET_BANNER_BY_CODE, setBannerUrl } from './setBanners';
export { SET_BINDER_COVER_BY_CODE, setBinderCoverUrl } from './setBinderCovers';
export { SET_LOGO_BY_CODE, setLogoUrl } from './setLogos';
export {
  USER_LIMITS,
  countCustomBinders,
  customBinderLimitReachedMessage,
  deckLimitReachedMessage,
  isAtCustomBinderLimit,
  isAtDeckLimit,
} from './userLimits';
